/**
 * Assistant run store: the seam between the pure agent core and the app.
 *
 * Everything the core needs is injected here and nowhere else — the LLM
 * transport (the Django proxy), the archive port (real pan.do/ra), the live
 * timeline, and the three sinks that write into the shared document. The core
 * itself still knows nothing about React, Zustand or Y.Doc.
 *
 * Attached and detached alongside chat/presence by state/project.ts, because the
 * Y.Doc is rebuilt on every connect.
 */
import { create } from 'zustand';
import type * as Y from 'yjs';
import { fetchAgentStatus, type AgentStatusResponse } from '../api/agent';
import { fetchArchives, semanticConfig, type Archive, type SemanticConfig } from '../api/archives';
import { createProxyTransport, LlmTransportError } from '../agent/core/llm';
import { runAgentTurn, type RunContext } from '../agent/core/loop';
import type { AgentEvent, LlmMessage } from '../agent/core/types';
import { briefArchives, buildSystemPrompt } from '../agent/context/prompt';
import { createArchivePort } from '../agent/tools/port';
import { createEmbeddingsPort } from '../agent/tools/embeddingsPort';
import type { EmbeddingsPort } from '../agent/tools/embeddingsTypes';
import { EMBEDDING_TOOLS } from '../agent/tools/semantic';
import { MUTATE_TOOLS } from '../agent/tools/edit';
import { PRESENT_TOOLS } from '../agent/tools/present';
import { READ_TOOLS } from '../agent/tools/search';
import type { ArchivePort } from '../agent/tools/catalog';
import type { PandoraWireCall } from '../api/pandora';
import {
  publishProposal,
  setStatus,
  updateProposal,
  writeTrace,
  type ProposalRecord,
  type TraceCall,
  type TraceStep,
} from '../crdt/agent';
import { postMessage, type Attachment } from '../crdt/chat';
import { clearSessionTraces, rememberRun, type StepRecord } from './agentTrace';
import { genId } from '../format/helpers';
import { useBin } from './bin';
import { usePlayback } from './playback';
import { localIdentity } from './presence';
import { useTimeline } from './timeline';

const ALL_TOOLS = [...READ_TOOLS, ...MUTATE_TOOLS, ...PRESENT_TOOLS];

/**
 * The semantic-search toggle is a per-person preference that costs tokens on
 * every run, so it is remembered per browser rather than per project or per
 * document. localStorage may be absent, blocked or throwing (private windows,
 * jsdom, cleared site data); every access is guarded and the default is off.
 */
const SEMANTIC_PREF_KEY = 'fabled.semanticSearch';

export function readSemanticPref(): boolean {
  try {
    return globalThis.localStorage?.getItem(SEMANTIC_PREF_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSemanticPref(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(SEMANTIC_PREF_KEY, on ? '1' : '0');
  } catch {
    /* a preference that cannot be saved is still honoured for this session */
  }
}

/** Fixed identity for assistant messages, so they read consistently in chat. */
const ASSISTANT_AUTHOR = { name: 'Assistant', color: '#a78bfa' };

/**
 * Why a run ended badly, in words the user can act on.
 *
 * The proxy's error `code` (backend/agent/views.py) is the branch point. A bare
 * "something went wrong" is what this replaces: during development it hid a 406
 * and a 500 behind identical text, and it would do the same to a user hitting
 * their budget.
 */
export interface RunNotice {
  kind: 'error' | 'stopped' | 'capped';
  message: string;
  /** Whether offering Retry makes any sense — it does not for a spent budget. */
  retryable: boolean;
  /** Provider/proxy detail, shown folded away rather than thrown out. */
  detail?: string;
}

/** Human sentences for the proxy's stable error codes. */
const CODE_MESSAGES: Record<string, { message: string; retryable: boolean }> = {
  disabled: {
    message: 'The assistant is switched off on the server.',
    retryable: false,
  },
  over_budget: {
    message: "You have used today's assistant token budget. It resets at 00:00 UTC.",
    retryable: false,
  },
  in_flight: {
    message: 'Another assistant request is still running. Wait for it to finish.',
    retryable: true,
  },
  too_large: {
    message: 'This conversation is too large to send. Start a new one.',
    retryable: false,
  },
  bad_request: {
    message: 'The assistant sent a malformed request — this is a bug.',
    retryable: false,
  },
  upstream_timeout: {
    message: 'The model provider timed out.',
    retryable: true,
  },
  upstream_error: {
    message: 'Could not reach the model provider.',
    retryable: true,
  },
  upstream_status: {
    message: 'The model provider rejected the request.',
    retryable: true,
  },
};

const CAP_MESSAGES: Record<string, string> = {
  'step-cap': 'The assistant reached its step limit for one run.',
  'tool-cap': 'The assistant reached its tool-call limit for one run.',
  timeout: 'The assistant ran out of time.',
};

function noticeFor(code: string | undefined, message: string): RunNotice {
  const known = code ? CODE_MESSAGES[code] : undefined;
  if (known) return { kind: 'error', message: known.message, retryable: known.retryable, detail: message };
  // Unmapped: show the real text rather than inventing a friendlier lie.
  return { kind: 'error', message, retryable: true };
}

interface AgentState {
  status: AgentStatusResponse;
  /** Composer toggle: send the next message to the assistant. */
  assistantMode: boolean;
  /**
   * Where semantic search lives, from the archives registry — null when the
   * backend has no pandora-embeddings service configured, which hides the
   * toggle entirely.
   */
  semantic: SemanticConfig | null;
  /** Composer toggle: give the next run the semantic tools (docs/agent.md §9). */
  semanticMode: boolean;
  /** True while a run this client started is in flight. */
  running: boolean;
  /** Text streaming into the pending bubble. */
  streamingText: string;
  /** Steps of the run in flight, or of the last one this client started. */
  steps: StepRecord[];
  /** The run those steps belong to; '' when none has started this session. */
  runId: string;
  notice: RunNotice | null;
  /** The last thing asked, so Retry can re-send it verbatim. */
  lastPrompt: string;
  /** Rolling conversation for the model — NOT the chat history humans see. */
  history: LlmMessage[];

  refreshStatus(): Promise<void>;
  setAssistantMode(on: boolean): void;
  setSemanticMode(on: boolean): void;
  ask(message: string): Promise<void>;
  stop(): void;
  retry(): Promise<void>;
  applyProposal(record: ProposalRecord): void;
  discardProposal(record: ProposalRecord): void;
  dismissNotice(): void;
}

let doc: Y.Doc | null = null;
let archives: Archive[] = [];
let port: ArchivePort | null = null;
let embeddingsPort: EmbeddingsPort | null = null;
let abort: AbortController | null = null;

/**
 * Which step the archive observer should attribute a request to.
 *
 * Safe as a single mutable pointer because the loop dispatches tool calls
 * strictly serially, and this port instance is the agent's alone — the rest of
 * the app calls api/pandora.ts without an observer, so its traffic never
 * reaches here.
 */
let currentStep: StepRecord | null = null;
/**
 * Requests made with no step in flight. `briefArchives` runs before the loop
 * yields anything, so its `init` calls would otherwise be dropped or, worse,
 * attributed to whatever step ran last.
 */
let setupCalls: PandoraWireCall[] = [];

const wireObserver = (call: PandoraWireCall): void => {
  if (currentStep) currentStep.calls.push(call);
  else setupCalls.push(call);
};

/** The setup requests as a step, so they are visible rather than silently lost. */
function setupStep(): StepRecord | null {
  if (!setupCalls.length) return null;
  return {
    seq: -1,
    toolCallId: '',
    tool: 'archive_config',
    kind: 'read',
    label: 'Load archive configuration',
    args: {},
    rawArgs: '',
    state: setupCalls.every((c) => c.ok) ? 'ok' : 'error',
    calls: setupCalls,
  };
}

/**
 * Steps as they should be shown and stored: setup first, then the run's own.
 *
 * Empty when the run called no tools. The setup requests are context for real
 * steps, not a trace in their own right — otherwise every "hello" would carry a
 * "show what the assistant did" row explaining that it loaded some config.
 */
function allSteps(steps: StepRecord[]): StepRecord[] {
  if (!steps.length) return [];
  const setup = setupStep();
  return setup ? [setup, ...steps] : steps;
}

export const useAgent = create<AgentState>((set, get) => ({
  status: { enabled: false },
  assistantMode: false,
  semantic: null,
  semanticMode: readSemanticPref(),
  running: false,
  streamingText: '',
  steps: [],
  runId: '',
  notice: null,
  lastPrompt: '',
  history: [],

  async refreshStatus() {
    set({ status: await fetchAgentStatus() });
  },

  setAssistantMode(on) {
    set({ assistantMode: on });
  },

  setSemanticMode(on) {
    writeSemanticPref(on);
    set({ semanticMode: on });
  },

  dismissNotice() {
    set({ notice: null });
  },

  async retry() {
    const prompt = get().lastPrompt;
    if (prompt) await get().ask(prompt);
  },

  stop() {
    const controller = abort;
    if (!controller) return;
    // Write the trace FIRST. Detaching below makes `isCurrent()` false for the
    // winding-down run, so its own end-of-run save never happens — and a run
    // you had to stop is the one you most want to be able to look at.
    //
    // This is a deliberate exception to "a stopped run leaves nothing behind":
    // that rule protects the timeline from half-finished edits, and a trace is
    // bookkeeping under AGENT_STATE_ORIGIN, not a change to the edit.
    const { runId, steps } = get();
    saveTrace(doc, runId, allSteps(steps), 'cancelled', localIdentity().name);

    // Detach BEFORE aborting, so nothing the winding-down run emits can land:
    // from here on `isCurrent()` is false for it and every write is dropped.
    abort = null;
    controller.abort();
    currentStep = null;
    // Stop has to mean stop NOW. The generator unwinds whenever it gets round
    // to it — and a step parked in an await that ignores the signal (an archive
    // fetch, a provider read) can outlive its run by seconds. Waiting for its
    // `finally` to clear `running` would leave the composer locked for exactly
    // as long as the dead run felt like taking.
    set({
      running: false,
      streamingText: '',
      notice: { kind: 'stopped', message: 'Stopped. Nothing was changed.', retryable: true },
    });
    // Release the shared status too, or collaborators keep seeing "working".
    if (doc) {
      setStatus(doc, { runId: '', state: 'idle', label: '', byName: localIdentity().name });
    }
  },

  async ask(message) {
    const body = message.trim();
    if (!body || !doc || get().running) return;

    const runId = genId('run');
    const identity = localIdentity();

    // The user's message goes into chat as a normal message first, so the
    // conversation reads the same for everyone whether or not they can see the
    // assistant UI.
    postMessage(doc, { kind: 'user', author: identity, body, runId });

    const controller = new AbortController();
    abort = controller;
    // A run owns the store only while it is still the current one. Once it has
    // been stopped, superseded or detached, every write it attempts is dropped:
    // its late text deltas would otherwise stream into the NEXT run's bubble,
    // its teardown would clear the new run's `running` flag, and its `history`
    // would replace the live conversation — sending the following question to
    // the model with the wrong context.
    const isCurrent = () => abort === controller;
    // A new run starts with an empty trace; the previous one has already been
    // saved, and stays readable from the document.
    currentStep = null;
    setupCalls = [];
    set({
      running: true,
      streamingText: '',
      steps: [],
      runId,
      notice: null,
      lastPrompt: body,
    });

    const timeline = useTimeline.getState();
    const liveDoc = doc;
    // Defaults to 'error': if we never reach the end of the loop, that is what
    // happened, and the trace should say so.
    let endReason = 'error';

    try {
      if (!port) port = createArchivePort(archives, wireObserver);
      // The semantic tools ride along only when the user asked for them AND
      // the server told us where the service is. Either missing = the run is
      // exactly the pre-semantic agent: same tools, same prompt, same cost.
      const semantic = get().semantic;
      if (!embeddingsPort && semantic) embeddingsPort = createEmbeddingsPort(semantic, wireObserver);
      const withSemantic = get().semanticMode && semantic !== null && embeddingsPort !== null;
      const briefs = await briefArchives(
        archives.map((a) => ({ key: a.key, name: a.name })),
        port,
      );
      // The bin stores concat in/out as inPoint/outPoint; the agent's shared
      // vocabulary is in/out, so translate here rather than teaching the core
      // about a second naming.
      const bin = useBin.getState().entries.map((e) => ({
        id: e.id,
        title: e.title,
        archive: e.archive,
        item: e.item,
        in: e.inPoint,
        out: e.outPoint,
      }));

      const ctx: RunContext = {
        runId,
        llm: createProxyTransport(),
        tools: withSemantic ? [...ALL_TOOLS, ...EMBEDDING_TOOLS] : ALL_TOOLS,
        port,
        ...(withSemantic && embeddingsPort ? { embeddings: embeddingsPort } : {}),
        archives: archives.map((a) => ({ key: a.key, name: a.name })),
        baseEdit: timeline.edit,
        live: {
          selection: timeline.selection,
          playhead: usePlayback.getState().currentTime,
          bin,
        },
        sinks: {
          postChat: (msg) => {
            if (!isCurrent()) return;
            postMessage(liveDoc, {
              kind: 'agent',
              author: ASSISTANT_AUTHOR,
              body: msg.body,
              runId,
              atts: msg.atts as Attachment[] | undefined,
            });
          },
          publishProposal: (draft) => {
            // A stopped run must not leave a proposal card behind: the user
            // said stop, and "nothing was changed" has to stay true.
            if (!isCurrent()) return '';
            const id = publishProposal(liveDoc, {
              runId,
              title: draft.title,
              summary: draft.summary,
              ops: draft.ops,
              by: identity.name,
            });
            // The card itself is a chat message carrying the proposal id, so it
            // sits in conversation order rather than in a separate list.
            postMessage(liveDoc, {
              kind: 'agent',
              author: ASSISTANT_AUTHOR,
              body: draft.summary,
              runId,
              atts: [{ type: 'proposal', proposalId: id }],
            });
            return id;
          },
          status: (s) => {
            // Shared so other collaborators see that someone is working. A
            // superseded run writing here would stamp its own dead runId over
            // the live one, so collaborators watch the wrong run.
            if (!isCurrent()) return;
            setStatus(liveDoc, {
              runId: s.runId,
              state: s.state,
              label: s.label,
              byName: identity.name,
            });
          },
        },
        systemPrompt: buildSystemPrompt({
          archives: briefs,
          edit: timeline.edit,
          playhead: usePlayback.getState().currentTime,
          selection: timeline.selection,
          bin,
          ...(withSemantic && semantic ? { semantic: { archive: semantic.archive } } : {}),
        }),
        history: get().history,
        signal: abort.signal,
      };

      const generator = runAgentTurn(ctx, body);
      let next = await generator.next();
      while (!next.done) {
        if (isCurrent()) applyEvent(set, next.value);
        next = await generator.next();
      }
      const result = next.value;
      if (!isCurrent()) return;

      // Post whatever the assistant said as a real chat message, so it survives
      // the run and is visible to everyone.
      const finalText = get().streamingText.trim();
      if (finalText) {
        postMessage(liveDoc, {
          kind: 'agent',
          author: ASSISTANT_AUTHOR,
          body: finalText,
          runId,
        });
      }
      // `applyEvent` has already recorded the specific failure from the error
      // event; do not overwrite it with something vaguer here.
      if (result.reason === 'cancelled') {
        set({ notice: { kind: 'stopped', message: 'Stopped. Nothing was changed.', retryable: true } });
      } else if (CAP_MESSAGES[result.reason]) {
        set({
          notice: {
            kind: 'capped',
            message: `${CAP_MESSAGES[result.reason]} Anything it finished is in the proposal above.`,
            retryable: false,
          },
        });
      }
      endReason = result.reason;
      set({ history: trimHistory(result.messages) });
    } catch (err) {
      // An abort surfaces here as a rejection; a run nobody is waiting on any
      // more should not raise an error banner over the run that replaced it.
      if (!isCurrent()) return;
      const message = err instanceof Error ? err.message : String(err);
      const code = err instanceof LlmTransportError ? err.code : undefined;
      set({ notice: noticeFor(code, message) });
    } finally {
      // Usage moved either way, so the budget readout and the toggle's enabled
      // state should reflect it — this is global server truth, not run state,
      // and is safe (and wanted) even from a superseded run.
      void get().refreshStatus();
      if (isCurrent()) {
        abort = null;
        currentStep = null;
        // In `finally` so a run that died in the transport still leaves a trace
        // — that is exactly the run someone will want to examine. Guarded by
        // isCurrent() so a superseded run does not publish one for work the
        // user has already moved on from.
        saveTrace(liveDoc, runId, allSteps(get().steps), endReason, identity.name);
        set({ running: false, streamingText: '' });
        if (liveDoc) {
          setStatus(liveDoc, { runId, state: 'idle', label: '', byName: identity.name });
        }
      }
    }
  },

  applyProposal(record) {
    const result = useTimeline.getState().applyAgentProposal(record.ops);
    if (!doc) return;
    if (!result.ok) {
      updateProposal(doc, record.id, { status: 'conflict', conflict: result.conflict });
      return;
    }
    const identity = localIdentity();
    updateProposal(doc, record.id, {
      status: 'applied',
      appliedBy: identity.name,
      appliedAt: Date.now(),
    });
    postMessage(doc, {
      kind: 'system',
      author: identity,
      body: `${identity.name} applied “${record.title}” (${record.ops.length} operation${
        record.ops.length === 1 ? '' : 's'
      })`,
    });
  },

  discardProposal(record) {
    if (!doc) return;
    updateProposal(doc, record.id, { status: 'discarded' });
  },
}));

/** Fold one run event into the store. */
function applyEvent(set: (fn: (s: AgentState) => Partial<AgentState>) => void, event: AgentEvent) {
  switch (event.type) {
    case 'text-delta':
      set((s) => ({ streamingText: s.streamingText + event.text }));
      break;
    case 'tool-start': {
      const step: StepRecord = {
        seq: event.seq,
        toolCallId: event.id,
        tool: event.tool,
        kind: event.kind,
        label: event.label,
        args: event.args,
        rawArgs: event.rawArgs,
        state: 'running',
        calls: [],
      };
      // Archive requests made from here until the matching result attach to
      // this step. Held by reference: `calls` is pushed into in place, and the
      // array identity is what the store's entry shares.
      currentStep = step;
      set((s) => ({ steps: [...s.steps, step] }));
      break;
    }
    case 'tool-result': {
      currentStep = null;
      // Match on seq, not the provider's tool-call id, which can repeat within
      // a run and would mark the earliest matching step instead of this one.
      set((s) => ({
        steps: s.steps.map((step) =>
          step.seq === event.seq
            ? {
                ...step,
                state: event.result.ok ? 'ok' : 'error',
                error: event.result.ok
                  ? undefined
                  : (event.result as { error: string }).error,
                ms: event.ms,
                content: event.content,
                result: event.result,
                opArgs: event.opArgs,
                ops: event.ops,
              }
            : step,
        ),
      }));
      break;
    }
    case 'error':
      set(() => ({ notice: noticeFor(event.code, event.message) }));
      break;
    default:
      break;
  }
}

/**
 * The part of a step worth keeping in the shared document: what was asked, and
 * what was asked of the archive. Response bodies and the model-facing result
 * stay in the session cache (state/agentTrace.ts).
 */
function compactStep(step: StepRecord): TraceStep {
  const out: TraceStep = {
    seq: step.seq,
    tool: step.tool,
    label: step.label,
    state: step.state === 'error' ? 'error' : 'ok',
  };
  if (step.error) out.error = step.error;
  if (step.ms !== undefined) out.ms = step.ms;
  if (Object.keys(step.args).length) out.args = step.args;
  if (step.opArgs) out.opArgs = step.opArgs;
  if (step.calls.length) {
    out.calls = step.calls.map(
      (c): TraceCall => ({
        site: c.site,
        action: c.action,
        data: c.data,
        ms: c.ms,
        ok: c.ok,
        ...(c.cached ? { cached: true } : {}),
        ...(c.httpStatus !== undefined ? { httpStatus: c.httpStatus } : {}),
        ...(c.apiStatus !== undefined ? { apiStatus: c.apiStatus } : {}),
        ...(c.bytes !== undefined ? { bytes: c.bytes } : {}),
        ...(c.error ? { error: c.error } : {}),
      }),
    );
  }
  return out;
}

/**
 * Persist a finished run's steps: full detail to this session's memory, the
 * compact record to the document. Called once per run — including for a run the
 * user stopped, which is the case most worth being able to look at.
 */
function saveTrace(
  target: Y.Doc | null,
  runId: string,
  steps: StepRecord[],
  endReason: string,
  by: string,
): void {
  if (!runId || !steps.length) return;
  rememberRun(runId, steps);
  if (!target) return;
  writeTrace(target, {
    runId,
    by,
    at: Date.now(),
    endReason,
    steps: steps.map(compactStep),
  });
}

/**
 * Keep the tail of the conversation for the next turn, dropping the system
 * prompt (rebuilt every time, because the timeline it describes has changed).
 */
function trimHistory(messages: LlmMessage[]): LlmMessage[] {
  const withoutSystem = messages.filter((m) => m.role !== 'system');
  // Never start the retained window on a tool message: its originating
  // assistant tool_calls message would be missing and providers reject that.
  let start = Math.max(0, withoutSystem.length - 12);
  while (start < withoutSystem.length && withoutSystem[start].role === 'tool') start += 1;
  return withoutSystem.slice(start);
}

export function attachAgent(nextDoc: Y.Doc): void {
  detachAgent();
  doc = nextDoc;
  void useAgent.getState().refreshStatus();
  void fetchArchives().then((list) => {
    archives = list;
    port = createArchivePort(list, wireObserver);
    const semantic = semanticConfig(list);
    embeddingsPort = semantic ? createEmbeddingsPort(semantic, wireObserver) : null;
    useAgent.setState({ semantic });
  });
}

export function detachAgent(): void {
  abort?.abort();
  abort = null;
  doc = null;
  port = null;
  embeddingsPort = null;
  currentStep = null;
  setupCalls = [];
  // The next document is a different room; its traces are not these.
  clearSessionTraces();
  useAgent.setState({
    running: false,
    streamingText: '',
    steps: [],
    runId: '',
    notice: null,
    history: [],
  });
}
